import { useState, useEffect, useRef, useCallback } from 'react';
import {
  LogLevel,
  Participant,
  PipecatClient,
  PipecatClientOptions,
  RTVIEvent,
  type TransportState,
  logger as pipecatLogger,
} from '@pipecat-ai/client-js';
import { DailyTransport, DailyEventCallbacks } from '@pipecat-ai/daily-transport';
import SoundUtils from '../utils/soundUtils';
import { authService } from '@/services/authService';
import { getNetworkState } from '@/utils/networkState';
import { analytics } from '@/services/analytics';
import { bookAnalytics } from '@/services/bookAnalytics';
import type {
  AnalyticsTrigger,
  VoiceEndContext,
  VoiceEndReason,
  VoiceStartContext,
} from '@/services/analyticsContext';
import { flushProgressQueue, getEffectiveProgress } from '@/utils/audioProgressUtils';
import { AudioPosition } from '@/types/audioPosition';
import type { Book } from '@/utils/bookRouting';
import { AI_CONTEXT_WORD_COUNT } from '@/utils/config';
import { VOICE_DISCONNECTED_EVENT } from '@/utils/platform';
import { parseAudioControlCommand, type AudioControlCommand } from '@/types/audioControl';
import { buildMarkers } from '@/utils/buildInfo';

// Interface for metric events to be stored before sending to the server
/**
 * How many debug lines are kept for the end-of-session dump. The buffer is
 * shipped over the RTVI control channel, which is not a log pipe — a session
 * that ran for minutes must not try to push its whole history through it.
 * The interesting window is always the connect handshake, which is the tail
 * at the point anything goes wrong.
 */
const VOICE_DEBUG_LOG_LINES = 120;

/**
 * Bounds on the two awaits in the connect/hang-up path that had none. The
 * /connect request is normally 0.7-2s and the Daily leave sub-second; neither
 * may hang forever, because a promise that never settles is the one way the
 * entry control's spinner outlives a session.
 */
const CONNECT_REQUEST_TIMEOUT_MS = 15_000;
const DISCONNECT_TIMEOUT_MS = 5_000;

/**
 * How long connect() waits, once /connect has answered, for the pre-ready
 * recorder to deliver its first chunk before joining anyway. Normally the
 * chunk arrived while /connect was in flight and the wait is zero; a recorder
 * that cannot start (no AudioWorklet, a dead audio context) costs one bounded
 * wait per connect and is stamped as such.
 */
const RECORDER_GATE_TIMEOUT_MS = 1_500;

class TimeoutError extends Error {
  constructor(what: string, ms: number) {
    super(`${what} did not settle within ${ms}ms`);
    this.name = 'TimeoutError';
  }
}

const withTimeout = <T>(promise: Promise<T>, ms: number, what: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      // The underlying promise may still settle later; keep that from
      // surfacing as an unhandled rejection once nobody is listening.
      promise.catch(() => {});
      reject(new TimeoutError(what, ms));
    }, ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });

interface Deferred {
  promise: Promise<void>;
  resolve: () => void;
}

const deferred = (): Deferred => {
  let resolve: () => void = () => {};
  const promise = new Promise<void>((r) => {
    resolve = r;
  });
  return { promise, resolve };
};

/**
 * The transport's pre-ready recorder, reached through its private field
 * because the package exposes no hook into it. Only the members used by the
 * wrappers in initializeClient are declared; the rest of its shape is left
 * alone.
 */
interface RecorderLike {
  begin(track: MediaStreamTrack): Promise<boolean>;
  record(
    chunkProcessor: (data: { mono: ArrayBuffer; raw: ArrayBuffer }) => void,
    chunkSize?: number,
  ): Promise<boolean>;
  end(): Promise<unknown>;
  source?: { context?: AudioContext } | null;
}

interface MetricEvent {
  metric: string;
  timeTaken: number;
}

type MicrophonePermissionState =
  | 'granted'
  | 'prompt'
  | 'denied'
  | 'unsupported'
  | 'unknown';

interface VoiceLifecycle {
  conversationId: number | null;
  conversationNumber: number | null;
  firstConversation: boolean | null;
  connectStartedAt: number;
  connectedAt: number | null;
  entryContext: VoiceStartContext;
  microphonePermissionState: MicrophonePermissionState;
  failureTracked: boolean;
  endTracked: boolean;
}

interface VoiceConnectResponse {
  session_id?: number;
  conversation_number?: number;
  first_conversation?: boolean;
  error?: string;
  message?: string;
}

const microphonePermissionState = async (): Promise<MicrophonePermissionState> => {
  if (!navigator.permissions?.query) return 'unsupported';

  try {
    const status = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    return status.state;
  } catch {
    return 'unsupported';
  }
};

const elapsedMilliseconds = (startedAt: number): number =>
  Math.max(0, Math.round(performance.now() - startedAt));

interface AudioPlayerInstance {
  getCurrentSentenceIndex: () => number;
  getLastPlaybackTime: () => number;
  getCurrentContext?: (count: number) => string;
  getCurrentPosition?: () => AudioPosition;
  pause?: () => void;
  isReady?: boolean;
  isPlayerReady?: () => boolean;
  bookInfo?: any;
  audioRef?: React.RefObject<HTMLAudioElement | null>;
}

export const useRTVIClient = (
  audioPlayerInstance: AudioPlayerInstance | null,
  book: Book | null,
  onSpeakerChanged: (speaker: 'local' | 'bot') => void,
  onAudioControl?: (command: AudioControlCommand) => void | Promise<void>,
) => {
  const bookRef = useRef(book);
  useEffect(() => {
    bookRef.current = book;
  }, [book]);
  // Connection state
  const [connectionStatus, setConnectionStatus] = useState<string>('Disconnected');
  const [isBuffering, setIsBuffering] = useState<boolean>(false);
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  // Mirror of debugLogs for the teardown path: disconnect() needs the lines as
  // they stand right now, and reading them from state there would give a
  // stale closure.
  const debugLogsRef = useRef<string[]>([]);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [showUsageLimitModal, setShowUsageLimitModal] = useState<boolean>(false);

  // Refs
  const rtviClientRef = useRef<PipecatClient | null>(null);
  const botAudioRef = useRef<HTMLAudioElement | null>(null);
  const startTimeRef = useRef<number>(0);
  const metricEventsRef = useRef<MetricEvent[]>([]);
  const hasBufferingStartedRef = useRef<boolean>(false);
  // Voice-latency diagnostics: tracks when bot audio is actually audible
  // client-side (post jitter buffer), per reply onset. See
  // server/docs/voice-startup-latency-design.md.
  const botAudioOnsetRef = useRef<{ lastAbove: number; onsetCount: number }>({
    lastAbove: 0,
    onsetCount: 0,
  });
  // Counts bot-started-speaking RTVI messages (sent by the bot the moment it
  // starts writing reply audio). onset-N minus started-speaking-N on this
  // same clock = the audio path delay (bot source buffer + network + jitter
  // buffer) with no cross-clock alignment error.
  const botSpeakingCountRef = useRef<number>(0);
  const voiceLifecycleRef = useRef<VoiceLifecycle | null>(null);
  const pendingEndContextRef = useRef<VoiceEndContext | null>(null);
  // The pre-ready recorder gate for the session being connected: resolved by
  // the first chunk the recorder delivers, awaited by connect() before the
  // Daily join. Replaced on every connect so a stale recorder cannot satisfy
  // a new session's gate.
  const recorderGateRef = useRef<Deferred>(deferred());
  const firstChunkSeenRef = useRef<boolean>(false);

  // Use a ref to always have the latest audio player instance
  const audioPlayerRef = useRef(audioPlayerInstance);
  const onAudioControlRef = useRef(onAudioControl);

  // Update ref whenever the instance changes
  useEffect(() => {
    audioPlayerRef.current = audioPlayerInstance;
  }, [audioPlayerInstance]);

  useEffect(() => {
    onAudioControlRef.current = onAudioControl;
  }, [onAudioControl]);

  useEffect(() => {
    const audioPlayer = audioPlayerRef.current;
    if (audioPlayer?.pause) {
      if (isBuffering) {
        console.log('isBuffering is true, pausing audio.');
        audioPlayer.pause();
      }
    }
  }, [isBuffering]);

  /**
   * Add a timestamped message to the debug log.
   *
   * The console line used to be commented out, which is how the AudioWorklet
   * failure under capacitor://localhost stayed invisible: this buffer went to
   * a React state array nothing rendered, the transport's own catch blocks are
   * silent, and pipecat's logger level was never set. Three layers of silence,
   * no console, no server, no Mixpanel. The prefix makes the stream filterable
   * in Safari Web Inspector, which is the only place a device tells you
   * anything.
   */
  const log = useCallback((message: string): void => {
    const timestampedMessage = `${new Date().toISOString()} - ${message}`;
    debugLogsRef.current = [...debugLogsRef.current.slice(-(VOICE_DEBUG_LOG_LINES - 1)), timestampedMessage];
    setDebugLogs(prev => [...prev, timestampedMessage]);
    console.log(`[voice] ${message}`);
  }, []);

  /**
   * Update the connection status display
   */
  const updateStatus = useCallback((status: string): void => {
    setConnectionStatus(status);
    log(`Status: ${status}`);
  }, [log]);

  /**
   * Update the buffering status display
   */
  const updateBufferingStatus = useCallback((status: string): void => {
    setIsBuffering(status === 'Yes');
    log(`BufferingStatus: ${status}`);
  }, [log]);

  const trackVoiceEvent = useCallback((
    eventName: string,
    properties: Record<string, unknown>,
  ) => {
    const currentBook = bookRef.current;
    if (currentBook) {
      const position = audioPlayerRef.current?.getCurrentPosition?.() ?? null;
      bookAnalytics.track(
        eventName,
        currentBook,
        properties,
        getEffectiveProgress(position),
      );
      return;
    }
    analytics.track(eventName, properties);
  }, []);

  const endContextFor = useCallback((
    endReason: VoiceEndReason,
    trigger: AnalyticsTrigger,
    inputMethod: VoiceEndContext['input_method'] = 'automatic',
  ): VoiceEndContext | null => {
    const lifecycle = voiceLifecycleRef.current;
    if (!lifecycle) return null;
    return {
      surface: lifecycle.entryContext.surface,
      source: lifecycle.entryContext.source,
      end_reason: endReason,
      trigger,
      input_method: inputMethod,
    };
  }, []);

  const finalizeVoiceLifecycle = useCallback((context?: VoiceEndContext | null) => {
    const lifecycle = voiceLifecycleRef.current;
    if (
      !lifecycle ||
      lifecycle.endTracked ||
      lifecycle.conversationId === null ||
      lifecycle.connectedAt === null
    ) {
      return;
    }

    const resolvedContext = context ?? pendingEndContextRef.current ?? {
      surface: lifecycle.entryContext.surface,
      source: lifecycle.entryContext.source,
      end_reason: 'disconnect' as const,
      trigger: 'transport_disconnect' as const,
      input_method: 'automatic' as const,
    };

    lifecycle.endTracked = true;
    trackVoiceEvent('Voice Chat Ended', {
      conversation_id: lifecycle.conversationId,
      conversation_number: lifecycle.conversationNumber,
      first_conversation: lifecycle.firstConversation,
      connection_latency_ms: Math.max(
        0,
        Math.round(lifecycle.connectedAt - lifecycle.connectStartedAt),
      ),
      session_duration_seconds: Math.max(
        0,
        Number(((performance.now() - lifecycle.connectedAt) / 1000).toFixed(1)),
      ),
      entry_surface: lifecycle.entryContext.surface,
      microphone_permission_state: lifecycle.microphonePermissionState,
      ...resolvedContext,
    });
  }, [trackVoiceEvent]);

  /**
   * Disconnect from the bot and clean up media resources
   */
  const disconnect = useCallback(async (context?: VoiceEndContext): Promise<void> => {
    if (context) {
      pendingEndContextRef.current = context;
    }
    finalizeVoiceLifecycle(context);

    try {
      if (rtviClientRef.current) {
        // Ship the debug buffer while the channel is still up. It goes to the
        // bot, which writes it to the journal beside the BOOT[...] marks —
        // the one place this investigation could actually read. Sent before
        // teardown because after disconnect() there is nothing to send on,
        // and best-effort because a failure here must never block the
        // teardown that releases the microphone.
        try {
          if (rtviClientRef.current.connected && debugLogsRef.current.length > 0) {
            rtviClientRef.current.sendClientMessage('client-debug-log', {
              lines: debugLogsRef.current,
              ...buildMarkers(),
            });
          }
        } catch (dumpError) {
          console.log(`[voice] could not send debug log: ${(dumpError as Error).message}`);
        }

        // Fully release the microphone BEFORE/AS we disconnect. On iOS, an
        // active capture keeps the whole tab's audio session in
        // `playAndRecord` ("voice chat") mode, which ducks and quiets all
        // other audio. Leaving the mic alive is the real reason audiobook
        // playback stays quiet after a chat — note the previous
        // `audioRef.volume = 0.99` "fix" was a no-op on iOS, where
        // HTMLMediaElement.volume is read-only.
        try {
          rtviClientRef.current.enableMic(false);
        } catch (micError) {
          log(`Could not disable mic: ${(micError as Error).message}`);
        }

        // Stop the local mic track explicitly — don't rely solely on
        // transport.disconnect() to release the capture device.
        try {
          const localAudioTrack = rtviClientRef.current.tracks()?.local?.audio;
          if (localAudioTrack) {
            localAudioTrack.stop();
            log('Stopped local microphone track');
          }
        } catch (trackError) {
          log(`Could not stop local mic track: ${(trackError as Error).message}`);
        }

        try {
          await withTimeout(
            rtviClientRef.current.disconnect(),
            DISCONNECT_TIMEOUT_MS,
            'transport disconnect',
          );
        } catch (disconnectError) {
          // A leave that never settles must not pin the entry control on its
          // hang-up spinner: note it and carry on with the local teardown.
          log(`Transport disconnect did not settle: ${(disconnectError as Error).message}`);
        }
        if (botAudioRef.current && botAudioRef.current.srcObject) {
          (botAudioRef.current.srcObject as MediaStream)
            .getAudioTracks()
            .forEach(track => track.stop());
          botAudioRef.current.srcObject = null;
        }
      }

      // Signal the audio player and visualizer to undo any lingering iOS
      // audio-session ducking now that capture has stopped. They re-acquire
      // the audio session in the correct (full-volume) playback category.
      document.dispatchEvent(new CustomEvent(VOICE_DISCONNECTED_EVENT));
      log('Dispatched voice_disconnected for audio-session recovery');

      // clear the metric events on disconnect
      metricEventsRef.current = [];
    } catch (error) {
      log(`Error disconnecting: ${(error as Error).message}`);
    } finally {
      // Authoritatively reset the voice-session UI state here instead of
      // relying solely on the transport's onDisconnected callback. An
      // interrupted session (transport error, bot subprocess dying, abnormal
      // teardown) may never fire onDisconnected, which leaves isConnected /
      // isBuffering stuck `true`. That wedges the play/pause button:
      // handlePlayPause then keeps taking its "stop the voice session" branch
      // and never reaches togglePlay(), so the audiobook can't be restarted.
      setIsConnected(false);
      updateBufferingStatus('No');
      hasBufferingStartedRef.current = false;
      pendingEndContextRef.current = null;
      voiceLifecycleRef.current = null;
    }
  }, [finalizeVoiceLifecycle, log, updateBufferingStatus]);

  /**
   * Initialize bot audio element
   */
  useEffect(() => {
    const audio = document.createElement('audio');
    audio.autoplay = true;
    botAudioRef.current = audio;
    document.body.appendChild(audio);

    return () => {
      if (botAudioRef.current && botAudioRef.current.parentNode === document.body) {
        document.body.removeChild(botAudioRef.current);
      }
      botAudioRef.current = null;
    };
  }, []);

  /**
   * Set up an audio track for playback
   * Handles both initial setup and track updates
   */
  const setupAudioTrack = useCallback((track: MediaStreamTrack): void => {
    log('Setting up audio track');
    if (botAudioRef.current && botAudioRef.current.srcObject) {
      const oldTrack = (botAudioRef.current.srcObject as MediaStream).getAudioTracks()[0];
      if (oldTrack?.id === track.id) return;
    }
    if (botAudioRef.current) {
      const stream = new MediaStream([track]);
      botAudioRef.current.srcObject = stream;

      log('RTVI audio stream attached');
    }
  }, [log]);


  /**
   * Set up listeners for track events (start/stop)
   * This handles new tracks being added during the session
   */
  const setupTrackListeners = useCallback((client: PipecatClient): void => {
    client.on(RTVIEvent.TrackStarted, (track, participant) => {
      if (!participant?.local && track.kind === 'audio') {
        setupAudioTrack(track);
      }
    });

    client.on(RTVIEvent.TrackStopped, (track, participant) => {
      log(`Track stopped: ${track.kind} from ${participant?.name || 'unknown'}`);
    });
  }, [setupAudioTrack, log]);


  /**
   * Check for available media tracks and set them up if present
   * This is called when the bot is ready or when the transport state changes to ready
   */
  const setupMediaTracks = useCallback(() => {
    if (!rtviClientRef.current) return;
    const tracks = rtviClientRef.current.tracks();
    if (tracks.bot?.audio) {
      setupAudioTrack(tracks.bot.audio);
    }
  }, [setupAudioTrack]);

  /**
   * Send all queued metrics to the server (only call once the client is ready. e.g., in onBotReady)
   */
  const sendAllMetricEvents = useCallback(() => {
    const client = rtviClientRef.current;
    const metricsToSend = metricEventsRef.current;

    if (client?.connected && metricsToSend.length > 0) {
      log(`Sending ${metricsToSend.length} aggregated metric events...`);

      // combine all stored metrics into a single event payload
      const combinedPayload = metricsToSend.reduce((acc, event) => {
        acc[event.metric] = event.timeTaken;
        return acc;
      }, {} as { [key: string]: number });

      // send a single client message containing all metrics
      client.sendClientMessage('client-metrics', combinedPayload);

      log(`Combined metrics sent: ${JSON.stringify(combinedPayload)}`);
      metricEventsRef.current = []; // clear the list after successful send
    }
  }, [log]);

  /**
   * Voice-latency diagnostics: log a timing event (ms since connect click)
   * and, when a metric name is given, forward it to the bot as a
   * client-metrics message so it lands in the server journal next to the
   * BOOT[...] marks.
   */
  const stampTiming = useCallback(
    (label: string, metric?: string, value?: number) => {
      const ms = Date.now() - startTimeRef.current;
      // A metric normally carries the elapsed time; `value` lets a quantity
      // (bytes at flush) ride the same channel.
      const reported = value ?? ms;
      log(`[timing] ${label}: ${ms}ms`);
      console.log(`[voice-timing] ${label}: ${ms}ms`);
      if (metric) {
        const client = rtviClientRef.current;
        let sent = false;
        if (client?.connected) {
          try {
            client.sendClientMessage('client-metrics', { [metric]: reported });
            sent = true;
          } catch {
            // e.g. transport connected but not yet ready — queue it below
          }
        }
        if (!sent) {
          // Queued events are flushed by sendAllMetricEvents at bot-ready.
          metricEventsRef.current.push({ metric, timeTaken: reported });
        }
      }
    },
    [log]
  );

  /**
   * Send audio context to the server
   */
  const sendAudioContext = useCallback((): void => {
    try {
      const audioPlayer = audioPlayerInstance;
      if (!audioPlayer) {
        log('Audio player instance not available');
        return;
      }
      const currentSentenceIndex = audioPlayer.getCurrentSentenceIndex();
      const currentTime = audioPlayer.getLastPlaybackTime();
      let sentenceText = "";

      if (audioPlayer.getCurrentContext) {
        sentenceText = audioPlayer.getCurrentContext(AI_CONTEXT_WORD_COUNT);
      }

      log(`Sending audio context: sentence #${currentSentenceIndex}, time: ${currentTime}, text: ${sentenceText}`);

      if (rtviClientRef.current?.connected) {
        rtviClientRef.current.sendClientMessage('audio-context', {
          id: `audio-context-${Date.now()}`,
          sentenceIndex: currentSentenceIndex,
          audioPosition: currentTime,
          sentenceText: sentenceText
        });
        log('Audio context sent successfully');
      } else {
        log('Cannot send audio context: RTVI client not connected');
      }
    } catch (error) {
      log(`Error sending audio context: ${error}`);
    }
  }, [log]);

  /**
   * Initialize PipecatClient with all original callbacks and configuration
   */
  useEffect(() => {
    const initializeClient = (): PipecatClient => {
      // NOTE: the stock transport's client-ready handshake waits for the bot's
      // audio track. That wait was once ~3s and an eager variant was written to
      // skip it; the variant sent client-ready before the room's media path was
      // negotiated and mic audio then took ~17s to reach the bot (first
      // utterances lost). It has since been deleted, because the wait it
      // targeted is now 236ms — the bot's track appears 79ms after its own
      // Daily join resolves, so the 3s was never track establishment, it was a
      // late bot (see the CORRECTION section of
      // server/docs/voice-startup-latency-design.md). Nothing to win here.
      const transport = new DailyTransport({
        bufferLocalAudioUntilBotReady: true,
      });

      // Voice-latency diagnostics on the raw Daily call: when do tracks
      // actually start, and when does bot audio become audible (post jitter
      // buffer)? `bot-audio-onset-N` per reply is the client-side sibling of
      // the bot's V2V[turn N] log line.
      const daily = (
        transport as unknown as {
          dailyCallClient?: {
            on(ev: string, cb: (ev: never) => void): void;
            startRemoteParticipantsAudioLevelObserver(intervalMs: number): Promise<void>;
          };
        }
      ).dailyCallClient;
      if (daily) {
        daily.on('joined-meeting', () => {
          // Forwarded as a metric so the client's OWN join time lands in the
          // server journal next to BOOT[daily_joined]: without it we cannot
          // tell whether pre-joining the bot alone takes the join off the
          // critical path, or whether the client's join becomes the new gate.
          stampTiming('daily joined-meeting', 'client-joined-latency');
          daily
            .startRemoteParticipantsAudioLevelObserver(100)
            .catch(() => log('[timing] audio level observer unavailable'));
        });
        daily.on('track-started', (ev: never) => {
          const e = ev as { participant?: { local?: boolean }; track?: { kind?: string } };
          const who = e.participant?.local ? 'local' : 'remote';
          const kind = e.track?.kind ?? '?';
          stampTiming(
            `track-started ${who} ${kind}`,
            who === 'remote' && kind === 'audio' ? 'bot-track-started-latency' : undefined
          );
        });
        daily.on('remote-participants-audio-level', (ev: never) => {
          const levels =
            (ev as { participantsAudioLevel?: Record<string, number> }).participantsAudioLevel ??
            {};
          const now = Date.now();
          const state = botAudioOnsetRef.current;
          for (const id in levels) {
            if (levels[id] > 0.01) {
              if (now - state.lastAbove > 1500) {
                state.onsetCount += 1;
                stampTiming(
                  `bot-audio-onset #${state.onsetCount}`,
                  `bot-audio-onset-${state.onsetCount}-latency`
                );
              }
              state.lastAbove = now;
              break;
            }
          }
        });
      } else {
        log('[timing] dailyCallClient not accessible; track diagnostics disabled');
      }

      const clientOptions: PipecatClientOptions = {
        transport: transport,
        enableMic: true,
        enableCam: false,
        callbacks: {
          onBotStartedSpeaking: () => {
            botSpeakingCountRef.current += 1;
            stampTiming(
              `bot-started-speaking #${botSpeakingCountRef.current}`,
              `bot-started-speaking-${botSpeakingCountRef.current}-latency`
            );
            onSpeakerChanged('bot');
          },
          onBotStoppedSpeaking: () => {
            onSpeakerChanged('local');
          },
          onTransportStateChanged: (state: TransportState) => {
            // `ready` is the edge the recorder races: the transport flushes
            // its pre-ready buffer once, right there, then sends client-ready.
            stampTiming(`transport ${state}`, state === 'ready' ? 'transport-ready-latency' : undefined);
          },
          onConnected: () => {
            updateStatus('Connected');
            setIsConnected(true);
            // Spinner watch: showSpinner is `isConnecting || isDisconnecting`
            // in ConnectButton/AlephEntry, and isConnecting clears on
            // `isConnected || isBuffering`. Stamping both of those here and in
            // onAudioBufferingStarted is what makes "did the wheel ever stop,
            // and when" answerable from the session record instead of from a
            // screencast.
            stampTiming('client connected', 'client-connected-latency');
            log('Client connected');
          },
          onDisconnected: () => {
            finalizeVoiceLifecycle();
            updateStatus('Disconnected');
            updateBufferingStatus('No');
            setIsConnected(false);
            log('Client disconnected');
            metricEventsRef.current = []; // clear the metric events on disconnect
            hasBufferingStartedRef.current = false; // reset buffering flag
          },
          onBotConnected: (participant: Participant) => {
            const timeTaken = Date.now() - startTimeRef.current;
            log(`onBotConnected, timeTaken: ${timeTaken}`);
            metricEventsRef.current.push({ metric: 'bot-connected-latency', timeTaken });
          },
          onBotReady: (data) => {
            const timeTaken = Date.now() - startTimeRef.current;
            log(`onBotReady, timeTaken: ${timeTaken}`);
            log(`Bot ready: ${JSON.stringify(data)}`);

            setupMediaTracks();
            metricEventsRef.current.push({ metric: 'bot-ready-latency', timeTaken });
            sendAllMetricEvents(); // send all collected metrics now that the transport is ready

            // Firefox fallback: If audio buffering hasn't started yet (due to sample rate issues),
            // play beep here so user knows they can talk
            if (!hasBufferingStartedRef.current) {
              hasBufferingStartedRef.current = true;
              updateBufferingStatus('Yes'); // Update UI to show buffering active (stops spinner)
              // Distinguishable from the real thing on purpose: reaching this
              // means capture never started and the spinner was cleared by the
              // fallback, with everything said during the connect window
              // dropped. A session with this and no mic-capture-latency is the
              // worklet signature.
              stampTiming('buffering fallback (no mic capture)', 'buffering-fallback-latency');
              SoundUtils.beep();
            }
          },
          onUserTranscript: (data) => {
            if (data.final) {
              log(`User: ${data.text}`);
            }
          },
          onServerMessage: (data) => {
            if (data && data.type === 'voice_session_end' && data.reason === 'timeout') {
              const timeoutContext = endContextFor('timeout', 'timeout');
              if (timeoutContext) void disconnect(timeoutContext);
              return;
            }
            if (data && data.type === 'control_audio') {
              log(`Received audio control: ${JSON.stringify(data)}`);
              const command = parseAudioControlCommand(data);
              const voiceContext = endContextFor('user_stop', 'voice_command', 'voice');
              void (async () => {
                await disconnect(voiceContext ?? undefined);
                if (command) await onAudioControlRef.current?.(command);
              })();
            }
          },
          onBotTranscript: (data) => log(`Bot: ${data.text}`),
          onMessageError: (error) => {
            console.error('Message error:', error);
            log(`Message error: ${JSON.stringify(error)}`);

            // Check if this is a usage limit error
            const errorStr = JSON.stringify(error);

            if (errorStr.includes('Usage limit exceeded') ||
                errorStr.includes('limit exceeded') ||
                errorStr.includes('url should be a string') ||
                errorStr.includes('Failed to join room')) {
              console.log('Usage limit detected in message error - showing modal');
              setShowUsageLimitModal(true);
              updateStatus('Usage limit exceeded');
              updateBufferingStatus('No');
              const limitContext = endContextFor('usage_limit', 'error');
              void disconnect(limitContext ?? undefined);
            }
          },
          onError: (error) => {
            console.error('RTVI Error callback:', error);
            log(`Error callback: ${JSON.stringify(error)}`);

            // Check if this is a usage limit error
            const errorStr = JSON.stringify(error);

            // Check for usage limit or malformed response (which likely means error response from server)
            if (errorStr.includes('Usage limit exceeded') ||
                errorStr.includes('limit exceeded') ||
                errorStr.includes('url should be a string') ||
                errorStr.includes('Failed to join room')) {
              console.log('Usage limit detected - showing modal');
              setShowUsageLimitModal(true);
              updateStatus('Usage limit exceeded');
              updateBufferingStatus('No');
              const limitContext = endContextFor('usage_limit', 'error');
              void disconnect(limitContext ?? undefined);
            } else {
              // Any other RTVI error means the session is effectively dead.
              // Tear it down so the UI recovers — disconnect() now resets
              // isConnected/isBuffering, preventing the stuck play/pause button
              // after an interrupted session.
              updateStatus('Error');
              const errorContext = endContextFor('error', 'error');
              void disconnect(errorContext ?? undefined);
            }
          },
          // Daily specific callbacks
          onAudioBufferingStarted: () => {
            hasBufferingStartedRef.current = true;
            updateBufferingStatus('Yes');
            // Via stampTiming rather than the queue: the queue is flushed once,
            // from onBotReady, so a capture that starts after bot-ready was
            // recorded nowhere and read back as null. Null then conflated
            // three different states — worklet failed, capture skipped because
            // the transport was already `ready`, or simply measured too late —
            // and a claim had to be retracted over it. stampTiming sends now
            // when the client is connected and queues only when it is not.
            stampTiming('mic capture started', 'mic-capture-latency');

            // Play beep to signal user can start talking
            SoundUtils.beep();
          },
          onAudioBufferingStopped: () => {
            updateBufferingStatus('No');
            log(`onMicCaptureStopped, timeTaken: ${Date.now() - startTimeRef.current}`);
          },
        } as DailyEventCallbacks, // cast to DailyEventCallbacks to include the audio buffering callbacks
      };

      const client = new PipecatClient(clientOptions);
      rtviClientRef.current = client;

      // The transport's pre-ready recorder fails silently — handleLocalAudioTrack
      // wraps begin()/record() in `catch (e) { // void }` — so a recorder that
      // never started has been indistinguishable from one that started late.
      // The recorder is created once by initialize() and reused across
      // sessions, so wrapping the instance here covers every connect. It
      // (a) surfaces those failures, (b) observes the first delivered chunk,
      // which is the only proof the audio context is rendering — the
      // onAudioBufferingStarted callback fires from a worklet port receipt
      // that arrives even when it is not — and (c) closes the AudioContext
      // that begin() creates and end() otherwise leaks, one per session, on a
      // platform (iOS) that caps how many can exist at once.
      const recorder = (transport as unknown as { _mediaStreamRecorder?: RecorderLike })
        ._mediaStreamRecorder;
      if (recorder) {
        const originalBegin = recorder.begin.bind(recorder);
        const originalRecord = recorder.record.bind(recorder);
        const originalEnd = recorder.end.bind(recorder);
        const describe = (error: unknown): string => {
          const e = error as { name?: string; message?: string } | null;
          return `${e?.name ?? 'Error'}: ${e?.message ?? String(error)}`;
        };
        recorder.begin = async (track) => {
          try {
            return await originalBegin(track);
          } catch (error) {
            log(`recorder begin failed: ${describe(error)}`);
            stampTiming('recorder begin failed', 'recorder-failure-latency');
            throw error;
          }
        };
        recorder.record = async (chunkProcessor, chunkSize) => {
          // Resolve the gate of the session this record() call belongs to,
          // not whichever is current when the chunk lands.
          const gate = recorderGateRef.current;
          let first = true;
          const observed: typeof chunkProcessor = (data) => {
            if (first) {
              first = false;
              gate.resolve();
              if (!firstChunkSeenRef.current) {
                firstChunkSeenRef.current = true;
                stampTiming('recorder first chunk', 'recorder-first-chunk-latency');
              }
            }
            chunkProcessor(data);
          };
          try {
            return await originalRecord(observed, chunkSize);
          } catch (error) {
            log(`recorder record failed: ${describe(error)}`);
            stampTiming('recorder record failed', 'recorder-failure-latency');
            throw error;
          }
        };
        recorder.end = async () => {
          const context = recorder.source?.context;
          try {
            return await originalEnd();
          } catch (error) {
            log(`recorder end failed: ${describe(error)}`);
            throw error;
          } finally {
            if (context && context.state !== 'closed') {
              context.close().catch(() => {
                // Already closing; nothing left to release.
              });
            }
          }
        };
      } else {
        log('[timing] recorder not accessible; recorder diagnostics disabled');
      }

      // How much pre-ready audio the transport hands to the bot at its single
      // flush, the moment it goes `ready`. Zero here alongside a
      // mic-capture-latency is the "recorder started, nothing arrived"
      // signature; the server-side counterpart is what _handle_audio_buffer
      // decodes.
      const flushing = transport as unknown as {
        _audioQueue?: ArrayBuffer[];
        flushAudioQueue?: () => void;
      };
      if (typeof flushing.flushAudioQueue === 'function') {
        const originalFlush = flushing.flushAudioQueue.bind(transport);
        flushing.flushAudioQueue = () => {
          const queue = flushing._audioQueue ?? [];
          const bytes = queue.reduce((total, chunk) => total + chunk.byteLength, 0);
          stampTiming(
            `pre-ready flush: ${queue.length} chunks, ${bytes} bytes`,
            'pre-ready-audio-bytes',
            bytes,
          );
          originalFlush();
        };
      }

      // pipecat's logger defaults to silence and nothing ever configured it,
      // so the transport's own diagnostics — including the audioWorklet load
      // failure that is the leading iOS hypothesis — went nowhere. WARN keeps
      // the noise down while letting anything that actually broke through.
      try {
        pipecatLogger.setLevel(LogLevel.WARN);
      } catch {
        // Logger shape is not part of the package's stable surface.
      }

      setupTrackListeners(client);
      return client;
    };

    const client = initializeClient();

    return () => {
      if (client) {
        finalizeVoiceLifecycle(endContextFor('navigation', 'navigation'));
        client.disconnect().catch(error => {
          log(`Error during cleanup disconnect: ${error}`);
        });
      }
    };
  }, [log, updateStatus, updateBufferingStatus, setupMediaTracks, setupTrackListeners, disconnect, endContextFor, finalizeVoiceLifecycle, setShowUsageLimitModal, sendAllMetricEvents, stampTiming]);

  /**
   * Initialize and connect to the bot
   * This sets up the RTVI client, initializes devices, and establishes the connection
   */
  const connect = useCallback(async (context: VoiceStartContext): Promise<boolean> => {
    const lifecycle: VoiceLifecycle = {
      conversationId: null,
      conversationNumber: null,
      firstConversation: null,
      connectStartedAt: performance.now(),
      connectedAt: null,
      entryContext: context,
      microphonePermissionState: 'unknown',
      failureTracked: false,
      endTracked: false,
    };
    voiceLifecycleRef.current = lifecycle;
    pendingEndContextRef.current = null;

    const trackConnectFailed = (reason: string) => {
      if (lifecycle.failureTracked) return;
      lifecycle.failureTracked = true;
      trackVoiceEvent('Voice Chat Connect Failed', {
        conversation_id: lifecycle.conversationId,
        conversation_number: lifecycle.conversationNumber,
        first_conversation: lifecycle.firstConversation,
        reason,
        connection_latency_ms: elapsedMilliseconds(lifecycle.connectStartedAt),
        entry_surface: context.surface,
        microphone_permission_state: lifecycle.microphonePermissionState,
        ...context,
      });
      if (voiceLifecycleRef.current === lifecycle) {
        voiceLifecycleRef.current = null;
      }
    };

    try {
      // Bail before the microphone prompt and the handshake: with no
      // connection the transport would spin until it timed out, which reads as
      // "this is broken" rather than "you are offline".
      const network = await getNetworkState();
      if (!network.isOnline) {
        log('No network connection; not attempting to connect.');
        updateStatus('No connection');
        trackConnectFailed('offline');
        return false;
      }

      lifecycle.microphonePermissionState = await microphonePermissionState();
      startTimeRef.current = Date.now();
      botAudioOnsetRef.current = { lastAbove: 0, onsetCount: 0 };
      botSpeakingCountRef.current = 0;
      recorderGateRef.current = deferred();
      firstChunkSeenRef.current = false;
      log('Connecting to bot...');

      if (!rtviClientRef.current) {
        trackConnectFailed('client_unavailable');
        return false;
      }

      // Check authentication and get access token
      const accessToken = authService.getAccessToken();
      if (!accessToken) {
        log('User not authenticated, redirecting to login');
        updateStatus('Authentication required');
        trackConnectFailed('auth');
        // Redirect to login
        window.location.href = '/login';
        return false;
      }

      // Push any queued progress writes before connecting, so the server-side
      // max-read marker the bot reads (semantic search / spoiler horizon) is
      // current. Fire-and-forget: the connect payload carries the live position
      // as a floor either way.
      void flushProgressQueue().catch(() => {});

      // Gather the book context synchronously first, so the /connect request
      // can be fired before (and concurrently with) device initialization.
      let sentenceText = "";
      let audioPosition: AudioPosition | null = null;
      const audioPlayer = audioPlayerRef.current; // Use ref instead of closure

      if (audioPlayer && audioPlayer.isPlayerReady && audioPlayer.isPlayerReady()) {
        if (audioPlayer.pause) {
          audioPlayer.pause();
        }
        if (audioPlayer.getCurrentContext) {
          sentenceText = audioPlayer.getCurrentContext(AI_CONTEXT_WORD_COUNT);
        }
        if (audioPlayer.getCurrentPosition) {
          audioPosition = audioPlayer.getCurrentPosition();
        }
      }

      // Send only the sentence context. Title/author/notes used to be prepended
      // here, but the server now composes that header from the catalog row (see
      // bot_llm_factory.format_book_identity) so voice and text chat name the
      // book identically and neither depends on the client sending it.
      const systemInstruction = sentenceText
        ? `Current context: ${sentenceText}`
        : "";
      // Use the catalog book id from the reader shell.
      const bookId = audioPlayer?.bookInfo?.id || "";

      log(`Auth token (first 20 chars): ${accessToken.substring(0, 20)}...`);
      log(`BookId: ${bookId}`);
      log(`Audio position: ${audioPosition ? JSON.stringify(audioPosition) : 'null'}`);

      // disconnect() turns the mic off with enableMic(false), and the transport
      // persists that as `startAudioOff` for the NEXT startCamera(). Left in
      // place, every session after the first in a process began device init
      // with the mic off and only requested it after initDevices resolved — a
      // fresh getUserMedia racing /connect, the Daily join and the bot's track,
      // with the pre-ready recorder losing whenever it lost that race. Re-arm
      // it before initDevices so startCamera() acquires the mic the way a
      // first session does: inside the tap's user gesture, before a join can
      // begin. Not in disconnect(), where it would re-acquire the mic right
      // after hanging up — the iOS ducking enableMic(false) exists to avoid.
      try {
        rtviClientRef.current.enableMic(true);
      } catch (micError) {
        log(`Could not re-arm mic: ${(micError as Error).message}`);
      }

      // Run device init and the /connect request CONCURRENTLY — getUserMedia
      // takes 0.8-3.5s and /connect ~0.7-2s, and neither needs the other.
      // initDevices() flips mediaState to 'initializing' synchronously, so
      // startBot()'s internal needsInit() check won't re-run a serial init.
      log('Starting local media capture...');
      const initDevicesPromise = rtviClientRef.current.initDevices();

      // An access token that aged out while the app sat idle fails /connect
      // with a 401, which sends the reader to /login. Refresh it here, after
      // initDevices() has claimed the mic inside the tap's gesture. A token
      // with time left, the common case, adds no await at all.
      let connectToken: string | null = accessToken;
      if (authService.isTokenExpiringSoon()) {
        // Awaited below; keeps a rejection during the refresh from being
        // reported as unhandled in the meantime.
        initDevicesPromise.catch(() => {});
        connectToken = await authService.getValidAccessToken();
        if (!connectToken) {
          log('Session rejected on refresh, redirecting to login');
          updateStatus('Authentication required');
          trackConnectFailed('auth');
          window.location.href = '/login';
          return false;
        }
      }

      const startBotPromise = rtviClientRef.current.startBot({
        endpoint: `${import.meta.env.VITE_VOICE_BASE_URL || ''}/connect`,
        headers: new Headers({
          'Authorization': `Bearer ${connectToken}`
        }),
        requestData: {
          systemInstruction: systemInstruction,
          bookId: bookId,
          audioPosition: audioPosition,
          bookInfo: bookRef.current as any,
          // Which bundle is talking, and from what. Stored on voice_sessions,
          // because the native bundle is frozen in the .ipa and drifts from
          // whatever is deployed — without this the code being read is not
          // provably the code that ran.
          ...buildMarkers(),
        }
      });

      try {
        await initDevicesPromise;
        log('Local media capture started');
      } catch (deviceError) {
        console.error('Device initialization error:', deviceError);
        log(`Device initialization failed: ${deviceError}`);
        updateStatus('Microphone access required');
        if (
          deviceError instanceof DOMException &&
          (deviceError.name === 'NotAllowedError' || deviceError.name === 'SecurityError')
        ) {
          lifecycle.microphonePermissionState = 'denied';
        }
        trackConnectFailed('mic_permission');
        // A bot may already have been started by the concurrent /connect; it
        // cleans itself up when no participant joins (conversation timeout).
        startBotPromise.catch(() => {});
        return false;
      }

      // in the new SDK, connection parameters are passed directly to connect()
      const connectionParams = await withTimeout(
        startBotPromise,
        CONNECT_REQUEST_TIMEOUT_MS,
        '/connect request',
      );
      // Bounds the start of the client's Daily join: it cannot begin until both
      // initDevices and /connect have resolved.
      stampTiming('connect-response', 'connect-response-latency');
      const response = connectionParams as VoiceConnectResponse;
      lifecycle.conversationId =
        typeof response?.session_id === 'number' ? response.session_id : null;
      lifecycle.conversationNumber =
        typeof response?.conversation_number === 'number'
          ? response.conversation_number
          : null;
      lifecycle.firstConversation =
        typeof response?.first_conversation === 'boolean'
          ? response.first_conversation
          : null;

      // Check if response contains an error (usage limit exceeded, etc.)
      if (connectionParams && typeof connectionParams === 'object' && 'error' in connectionParams) {
        const errorResponse = connectionParams as any;
        log('Server returned error: ' + errorResponse.error);
        if (errorResponse.error === 'Usage limit exceeded') {
          log('Usage limit exceeded - showing modal');
          updateStatus('Usage limit exceeded');
          trackConnectFailed('usage_limit');
          setShowUsageLimitModal(true);
          updateBufferingStatus('No');
          return false;
        }
        throw new Error(errorResponse.message || errorResponse.error);
      }

      // Do not join until the pre-ready recorder has delivered a chunk. The
      // transport flushes its buffer exactly once, when it goes `ready`, and
      // skips the recorder entirely if the local track arrives after that — so
      // a recorder still starting when the join completes runs for the whole
      // session and never sends a byte. Waiting here makes that race
      // unlosable rather than usually won. Normally the chunk arrived while
      // /connect was in flight and this resolves at once.
      const gate = await Promise.race([
        recorderGateRef.current.promise.then(() => 'chunk' as const),
        new Promise<'timeout'>((resolve) => {
          window.setTimeout(() => resolve('timeout'), RECORDER_GATE_TIMEOUT_MS);
        }),
      ]);
      if (gate === 'timeout') {
        stampTiming(
          'recorder gate timed out; joining without pre-ready capture',
          'recorder-gate-timeout-latency',
        );
      } else {
        stampTiming('recorder gate passed');
      }

      // Connect with the returned parameters
      await rtviClientRef.current.connect(connectionParams);
      lifecycle.connectedAt = performance.now();
      lifecycle.microphonePermissionState = 'granted';
      trackVoiceEvent('Voice Chat Started', {
        conversation_id: lifecycle.conversationId,
        conversation_number: lifecycle.conversationNumber,
        first_conversation: lifecycle.firstConversation,
        connection_latency_ms: elapsedMilliseconds(lifecycle.connectStartedAt),
        entry_surface: context.surface,
        microphone_permission_state: lifecycle.microphonePermissionState,
        ...context,
      });
      return true;

    } catch (error: any) {
      console.error('RTVI Connect Error:', error);
      log(`Error connecting: ${JSON.stringify(error)}`);

      const errorMessage = error?.message || '';

      // Try to parse error as JSON if it's a string
      let errorData: any = null;
      try {
        if (typeof error === 'string') {
          errorData = JSON.parse(error);
        } else if (error?.error) {
          errorData = error;
        } else if (errorMessage) {
          try {
            errorData = JSON.parse(errorMessage);
          } catch (e) {
            // Not JSON, continue
          }
        }
      } catch (e) {
        // Not JSON, continue
      }

      // Handle 401 authentication errors
      if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        log('Authentication failed, redirecting to login');
        updateStatus('Authentication required');
        trackConnectFailed('auth');
        window.location.href = '/login';
        return false;
      }

      // Handle usage limit exceeded errors (should be caught before connect, but keeping as fallback)
      if (errorMessage.includes('Usage limit exceeded') ||
          errorMessage.includes('limit exceeded')) {
        log('Usage limit exceeded - showing modal');
        updateStatus('Usage limit exceeded');
        trackConnectFailed('usage_limit');
        setShowUsageLimitModal(true);
        updateBufferingStatus('No');
        return false;
      }

      updateStatus('Error');
      updateBufferingStatus('No');
      trackConnectFailed(error instanceof TimeoutError ? 'connect_timeout' : 'generic');

      if (rtviClientRef.current) {
        try {
          await rtviClientRef.current.disconnect();
        } catch (disconnectError) {
          log(`Error during disconnect: ${disconnectError}`);
        }
      }
      return false;
    }
  }, [log, trackVoiceEvent, updateStatus, updateBufferingStatus]);

  return {
    connect,
    disconnect,
    connectionStatus,
    isConnected,
    isBuffering,
    debugLogs,
    log,
    showUsageLimitModal,
    setShowUsageLimitModal,
    rtviClientRef,
  };
};
